import React, { useState } from 'react';
import { leerVehiculo } from '../../constants/vehiculoDeFlota';
import { View, Text, StyleSheet, SafeAreaView, ScrollView, TouchableOpacity, Alert, useWindowDimensions, Platform, Modal, TextInput, Linking } from 'react-native';
import { Colors, SIZES } from '../../constants/theme';
// Los datos de contacto NUNCA se escriben a mano aquí: viven centralizados.
import { COBROS_EN_LA_APP } from '../../constants/featureFlags';
import { whatsappSoporte, SOPORTE_TELEFONO } from '../../constants/contacto';
import { Ionicons } from '@expo/vector-icons';
import ScreenBackground from '../../components/ScreenBackground';
import AvisoVistaPrevia from '../../components/AvisoVistaPrevia';
import { GlassView } from 'expo-glass-effect';
import { useAppStore } from '../../store/useAppStore';
import * as Clipboard from 'expo-clipboard';
import { useRouter } from 'expo-router';
import { normalizarPlaca, revisarPlaca } from '../../utils/placa';
import { mensajeDeError } from '../../utils/mensajeDeError';
import { pesos } from '../../utils/dinero';

export default function DashboardScreen() {
  const { width } = useWindowDimensions();
  const isDesktop = width > 768;
  const router = useRouter();

  const vehicles = useAppStore((state) => state.rentCarVehicles);
  const drivers = useAppStore((state) => state.rentCarDrivers);
  const rentCarReminders = useAppStore((state) => state.rentCarReminders);
  const resolveReminder = useAppStore((state) => state.resolveReminder);
  const addRentCarVehicle = useAppStore((state) => state.addRentCarVehicle);
  const removeRentCarVehicle = useAppStore((state) => state.removeRentCarVehicle);

  // Sin esto el dueño puede pulsar «Guardar» cinco veces mientras el servidor
  // contesta, y acabar con cinco carros iguales en el inventario.
  const [guardandoVehiculo, setGuardandoVehiculo] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ brand: '', model: '', plate: '', price: '' });

  const activeVehiclesCount = vehicles.length;
  const assignedDriversCount = drivers.length;
  const billableVehiclesCount = Math.max(0, activeVehiclesCount - 3);
  const crmBillingAmount = billableVehiclesCount * 300;
  
  const rentedVehicles = vehicles.filter(v => v.status === 'Rentado');
  // PROYECCIÓN, no dinero cobrado: la tarifa semanal de los vehículos que están
  // rentados ahora mismo × 4 semanas. La app no registra cobros del rent car,
  // así que presentarlo como "ingresos del mes" sería afirmar algo que no
  // sabemos. El rótulo de la tarjeta lo dice con esas palabras.
  const monthlyRevenue = rentedVehicles.reduce((sum, v) => sum + v.price, 0) * 4;

  // LA GRÁFICA SEMANAL SE ELIMINÓ. Eran siete barras inventadas
  // (10%, 12%, 15%, 18%, 25%, 30%, 20% del total, fijas), con pinta de ser el
  // reparto real de ingresos por día. La app no tiene ni un solo dato diario
  // del rent car: era un dibujo, no un dato, y en una pantalla de dinero eso no
  // puede estar.

  const handleCopyLink = async () => {
    const webLink = 'https://gosafely-3e0e7.web.app/partner';
    if (Platform.OS !== 'web') {
      await Clipboard.setStringAsync(webLink);
      Alert.alert('Copiado', 'Enlace web copiado al portapapeles. Abrelo en tu PC.');
    } else {
      navigator.clipboard.writeText(webLink);
      window.alert('Enlace web copiado al portapapeles.');
    }
  };

  // Exportación CSV del inventario. Reemplaza el botón "Exportar Excel" que
  // antes NO tenía onPress — Google Play rechazó por elementos de UI que no
  // responden (Broken Functionality). En native copia el CSV al portapapeles
  // (Excel abre CSV pegado sin problema); en web fuerza descarga real.
  const handleExportCsv = async () => {
    if (vehicles.length === 0) {
      Alert.alert('Sin vehículos', 'Agrega al menos un vehículo antes de exportar.');
      return;
    }
    const header = 'Placa,Marca,Modelo,Estado,Precio Semanal (RD$)';
    const rows = vehicles.map(v =>
      // ⚠️ `v.brand` y `v.plate` solo existen en los carros dados de alta por
      // el formulario que ESTA pantalla tenía. Los de Flota guardan `make` y
      // (hasta hoy) ninguna placa, así que el CSV que el dueño exporta salía
      // con dos columnas vacías justo para esos. `leerVehiculo` lo cuadra.
      (() => { const c = leerVehiculo(v); return [c.plate, c.make, c.model, c.status, c.price]; })()
        .map(x => `"${String(x ?? '').replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = [header, ...rows].join('\n');
    try {
      if (Platform.OS === 'web') {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventario-vehiculos-${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        await Clipboard.setStringAsync(csv);
        Alert.alert(
          'Inventario copiado',
          `${vehicles.length} vehículos copiados como CSV. Pégalo en Excel o Google Sheets para verlos en tabla.`
        );
      }
    } catch (e: any) {
      Alert.alert('Error al exportar', mensajeDeError(e, 'No pudimos exportar el inventario. Intenta de nuevo.'));
    }
  };

  // El formulario de alta que colgaba de aquí se fue a Flota, que es el único
  // que queda (ver la nota grande más abajo, donde estaba su modal). Con él se
  // fue este manejador: `addRentCarVehicle` sigue existiendo para quien lo
  // necesite, y el aviso del cupo del 4º vehículo se mudó a `fleet.tsx`, que es
  // donde ahora se dan de alta los carros.

  const renderStatsRow = () => (
    <View style={styles.statsContainer}>
      <GlassView style={styles.statCard} colorScheme="dark">
        <Ionicons name="car" size={28} color={Colors.primary} />
        <Text style={styles.statValue}>{activeVehiclesCount}</Text>
        <Text style={styles.statLabel}>Vehículos Activos</Text>
      </GlassView>

      <GlassView style={styles.statCard} colorScheme="dark">
        <Ionicons name="people" size={28} color={Colors.accent} />
        <Text style={styles.statValue}>{assignedDriversCount}</Text>
        <Text style={styles.statLabel}>Choferes Asignados</Text>
      </GlassView>

      {isDesktop && (
        <GlassView style={stylesDesktop.statCard} colorScheme="dark">
          <Ionicons name="cash" size={36} color="#4CAF50" />
          <Text style={styles.statValue}>{pesos(monthlyRevenue)}</Text>
          <Text style={styles.statLabel}>Proyección del mes</Text>
        </GlassView>
      )}
    </View>
  );

  const renderRevenueCard = () => (
    <GlassView style={styles.statCardLarge} colorScheme="dark">
      <View style={styles.cardHeader}>
        <Ionicons name="wallet" size={24} color="#4CAF50" />
        {/* "Ingresos del Mes" afirmaba dinero cobrado. Es una proyección. */}
        <Text style={styles.cardTitle}>Proyección del mes</Text>
      </View>
      <Text style={styles.revenueValue}>{pesos(monthlyRevenue)}</Text>
      <Text style={styles.revenueSub}>
        {rentedVehicles.length} vehículo(s) rentado(s) × 4 semanas a su tarifa semanal
      </Text>
      <Text style={{ fontSize: 11, color: Colors.textSecondary, marginTop: 8, lineHeight: 16 }}>
        Es una estimación con las tarifas que registraste, no dinero cobrado: la app no
        lleva los pagos de tus clientes de rent car.
      </Text>
    </GlassView>
  );

  const renderBillingCard = () => (
    <GlassView style={styles.statCardLarge} colorScheme="dark">
      <View style={styles.cardHeader}>
        <Ionicons name="card" size={24} color={Colors.accent} />
        <Text style={styles.cardTitle}>Facturación de CRM</Text>
        <View style={styles.activePlanBadge}>
          <Text style={styles.activePlanBadgeText}>Plan Flexible</Text>
        </View>
      </View>
      <View style={styles.billingRow}>
        <View>
          <Text style={styles.billingValue}>{pesos(crmBillingAmount)}/mes</Text>
          {activeVehiclesCount <= 3 ? (
            // Con 0 vehículos, "¡Tienes 0 vehículos gratis!" no dice nada.
            <Text style={[styles.billingSub, { color: '#4CAF50', fontWeight: 'bold' }]}>
              {activeVehiclesCount === 0
                ? 'Tus primeros 3 vehículos son gratis.'
                : `Tus ${activeVehiclesCount} vehículo(s) están dentro de los 3 gratis.`}
            </Text>
          ) : (
            <View>
              <Text style={styles.billingSub}>{billableVehiclesCount} vehículo(s) por encima de los 3 gratis × RD$ 300.</Text>
              {/* Antes decía "Stripe ajustará tu factura (Per-seat pricing)".
                  No hay suscripción de Stripe por vehículo ni cobro automático:
                  el monto se paga por transferencia con comprobante. */}
              <Text style={{ fontSize: 11, color: Colors.textSecondary, marginTop: 4 }}>
                El monto sube o baja con los vehículos que tengas activos. Se paga por
                transferencia con comprobante, no se te cobra automáticamente.
              </Text>
            </View>
          )}
        </View>
        {/* Este botón abría https://dashboard.stripe.com — el panel de comercio
            de Stripe, donde el dueño del rent car no tiene ninguna cuenta: un
            botón que no lleva a ninguna parte. Ahora lleva a donde de verdad se
            resuelve el pago del cupo: soporte por WhatsApp. */}
        {/* ⚠️ EN iOS NO SALE. «Cómo pagar» abre WhatsApp para coordinar el
            pago del cupo, y hasta que ese cupo se paga el 4º vehículo no
            aparece en el marketplace DE LA APP. O sea: una llamada a la acción
            hacia un pago externo que desbloquea algo interno — 3.1.1.
            Ver COBROS_EN_LA_APP en constants/featureFlags.ts. */}
        {COBROS_EN_LA_APP && (
          <TouchableOpacity
            style={styles.manageBillingBtn}
            onPress={() => {
              Linking.openURL(
                whatsappSoporte('Hola, quiero coordinar el pago del cupo de mis vehículos en el CRM de Rent Car.')
              ).catch(() => {
                Alert.alert(
                  'No pudimos abrir WhatsApp',
                  `Escríbenos al ${SOPORTE_TELEFONO} para coordinar el pago del cupo.`
                );
              });
            }}
          >
            <Text style={styles.manageBillingBtnText}>Cómo pagar</Text>
          </TouchableOpacity>
        )}
      </View>
    </GlassView>
  );

  const renderWebBridgeCard = () => (
    <GlassView style={styles.statCardLarge} colorScheme="dark">
      <View style={[styles.cardHeader, { justifyContent: 'space-between' }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Ionicons name="desktop" size={24} color={Colors.primary} />
          <Text style={styles.cardTitle}>Go Safely Web</Text>
        </View>
      </View>
      <Text style={styles.webBridgeDesc}>
        Gestiona tu flota, choferes y finanzas desde tu computadora (Pantalla Grande).
      </Text>
      <View style={styles.webBridgeSteps}>
        <Text style={styles.webBridgeStepText}>Ingresa al siguiente enlace desde tu PC:</Text>
        <Text style={{fontWeight: 'bold', color: Colors.primary, marginTop: 8}}>https://gosafely-3e0e7.web.app/partner</Text>
      </View>
      <TouchableOpacity style={styles.scanButton} onPress={handleCopyLink}>
        <Ionicons name="copy-outline" size={20} color="#FFF" />
        <Text style={styles.scanButtonText}>Copiar Enlace para PC</Text>
      </TouchableOpacity>
    </GlassView>
  );

  const renderReminders = () => (
    <View>
      <Text style={styles.sectionTitle}>Recordatorios Pendientes</Text>
      {rentCarReminders.length === 0 ? (
        <Text style={{ color: Colors.textSecondary, fontSize: 14, fontStyle: 'italic', paddingVertical: 10 }}>
          No tienes recordatorios pendientes.
        </Text>
      ) : (
        rentCarReminders.map((reminder) => (
          <TouchableOpacity 
            key={reminder.id} 
            onPress={() => {
              if (Platform.OS === 'web') {
                const confirmed = window.confirm(`¿Deseas marcar este recordatorio como resuelto?\n\n"${reminder.title}"`);
                if(confirmed) resolveReminder(reminder.id);
              } else {
                Alert.alert(
                  'Resolver Recordatorio',
                  `¿Marcar como resuelto?\n\n"${reminder.title}"`,
                  [
                    { text: 'Cancelar', style: 'cancel' },
                    { text: 'Resuelto', onPress: () => resolveReminder(reminder.id) }
                  ]
                );
              }
            }}
          >
            <GlassView style={styles.reminderCard} colorScheme="dark">
              <Ionicons 
                name={reminder.type === 'PAGO' ? 'warning' : 'build'} 
                size={24} 
                color={reminder.type === 'PAGO' ? '#FF9800' : '#2196F3'} 
              />
              <View style={styles.reminderTextContainer}>
                <Text style={styles.reminderTitle}>{reminder.title}</Text>
                <Text style={styles.reminderSub}>{reminder.subtitle}</Text>
              </View>
            </GlassView>
          </TouchableOpacity>
        ))
      )}
    </View>
  );

  const renderDesktopDataTable = () => (
    <GlassView style={stylesDesktop.tableCard} colorScheme="dark">
      <Text style={styles.sectionTitle}>Inventario de Vehículos</Text>
      
      <View style={stylesDesktop.tableHeader}>
        <Text style={[stylesDesktop.tableCell, { flex: 2, fontWeight: 'bold', color: Colors.textSecondary }]}>Placa</Text>
        <Text style={[stylesDesktop.tableCell, { flex: 3, fontWeight: 'bold', color: Colors.textSecondary }]}>Marca / Modelo</Text>
        <Text style={[stylesDesktop.tableCell, { flex: 2, fontWeight: 'bold', color: Colors.textSecondary }]}>Estado</Text>
        <Text style={[stylesDesktop.tableCell, { flex: 2, fontWeight: 'bold', color: Colors.textSecondary }]}>Ingreso / Sem</Text>
        <Text style={[stylesDesktop.tableCell, { flex: 1, fontWeight: 'bold', color: Colors.textSecondary, textAlign: 'center' }]}>Acción</Text>
      </View>
      
      {vehicles.map((v, i) => (
        <View key={i} style={stylesDesktop.tableRow}>
          {/* Misma razón que en el CSV: un carro de Flota no tiene `brand`,
              así que esta columna salía en blanco. */}
          <Text style={[stylesDesktop.tableCell, { flex: 2, color: Colors.text }]}>{leerVehiculo(v).plate || '—'}</Text>
          <Text style={[stylesDesktop.tableCell, { flex: 3, color: Colors.text }]}>{leerVehiculo(v).titulo}</Text>
          <View style={{ flex: 2, padding: 15, justifyContent: 'center' }}>
            <View style={[stylesDesktop.statusBadge, v.status === 'Disponible' ? { backgroundColor: 'rgba(76, 175, 80, 0.2)' } : { backgroundColor: 'rgba(255, 152, 0, 0.2)' }]}>
              <Text style={[stylesDesktop.statusText, { color: v.status === 'Disponible' ? '#4CAF50' : '#FF9800' }]}>{v.status}</Text>
            </View>
          </View>
          <Text style={[stylesDesktop.tableCell, { flex: 2, color: Colors.primary, fontWeight: 'bold' }]}>{pesos(v.price)}</Text>
          <TouchableOpacity 
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            onPress={() => {
              // Mismo texto falso que en el alta: aquí prometía que "Stripe
              // dejará de cobrarte automáticamente". No hay cobro automático
              // que detener. Lo cierto es que el cupo del mes siguiente se
              // calcula con los vehículos que queden.
              if (Platform.OS === 'web') {
                const confirmed = window.confirm(`¿Seguro que deseas eliminar este vehículo (${leerVehiculo(v).plate || leerVehiculo(v).titulo})?\n\nEl cupo del mes siguiente se calcula con los vehículos que te queden activos.`);
                if(confirmed) removeRentCarVehicle(v.id);
              } else {
                Alert.alert(
                  'Eliminar Vehículo',
                  `¿Seguro que deseas eliminar el vehículo ${leerVehiculo(v).plate || leerVehiculo(v).titulo}?\n\nEl cupo del mes siguiente se calcula con los vehículos que te queden activos.`,
                  [
                    { text: 'Cancelar', style: 'cancel' },
                    { text: 'Eliminar', onPress: () => removeRentCarVehicle(v.id), style: 'destructive' }
                  ]
                );
              }
            }}
          >
            <Ionicons name="trash-outline" size={20} color="#F44336" />
          </TouchableOpacity>
        </View>
      ))}

      <TouchableOpacity style={stylesDesktop.exportBtn} onPress={handleExportCsv}>
        <Ionicons name="download-outline" size={18} color="#FFF" />
        <Text style={{ color: '#FFF', fontWeight: 'bold', marginLeft: 8 }}>Exportar Excel</Text>
      </TouchableOpacity>
    </GlassView>
  );

  // VISTA UNIVERSAL (Móvil y Web unificadas)
  return (
    <ScreenBackground>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView contentContainerStyle={styles.container}>
          <AvisoVistaPrevia que="el panel de un Rent Car" />
          {/* NI EL TEXTO NI EL BOTÓN PODÍAN ENCOGER, así que a 375 px el botón
              se montaba encima de «Resumen de tu Rent Car» y lo tapaba a la
              mitad.
              Primero se probó `flex: 1` en la columna de texto — y MEDIDO a
              375 px quedó peor: la columna se encogía a 157 px y «Dashboard»,
              que va a 32 px, se partía en dos líneas, «Dashboar / d».
              Lo que manda ahora es que la FILA pueda envolverse y que la
              columna tenga un ancho mínimo: si los dos no caben, el botón baja
              entero a la línea siguiente. Ni se solapan ni se parte nada. */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, gap: 10 }}>
            <View style={{ flexGrow: 1, flexShrink: 1, minWidth: 170 }}>
              <Text style={styles.title}>Dashboard</Text>
              <Text style={styles.subtitle}>Resumen de tu Rent Car</Text>
            </View>
            <TouchableOpacity 
              style={{ backgroundColor: Colors.primary, paddingHorizontal: 15, paddingVertical: 10, borderRadius: 12, flexDirection: 'row', alignItems: 'center', flexShrink: 0 }}
              onPress={() => router.push('/(tabs)/fleet' as any)}
            >
              <Ionicons name="add" size={20} color="#FFF" />
              <Text style={{ color: '#FFF', fontWeight: 'bold', marginLeft: 5 }}>Agregar Vehículo</Text>
            </TouchableOpacity>
          </View>

          {renderStatsRow()}

          <View style={styles.statsContainer}>
            {renderRevenueCard()}
          </View>

          <View style={styles.statsContainer}>
            {renderWebBridgeCard()}
          </View>

          <View style={[styles.statsContainer, { marginBottom: 25 }]}>
            {renderBillingCard()}
          </View>

          {renderReminders()}

          {/* Espacio extra abajo */}
          <View style={{ height: 100 }} />
        </ScrollView>
      </SafeAreaView>

      {/* Modal para Agregar Vehículo.
          onRequestClose (H37, auditoría 16-sep-2026): sin él, en Android el
          botón Atrás se lo tragaba el Modal y no pasaba nada. Hace lo mismo
          que la X del encabezado. */}
      {/* ══════════════════════════════════════════════════════════════════
          AQUÍ VIVÍA UN SEGUNDO FORMULARIO DE ALTA. BORRADO A PROPÓSITO.
          ══════════════════════════════════════════════════════════════════
          Pedía marca, modelo, placa y precio, y guardaba con NOMBRES DE CAMPO
          DISTINTOS a los del formulario de Flota: escribía `brand` donde el
          otro escribe `make`, y no escribía ni `year` ni `carImage`. El
          Marketplace lee `make`, `year` y `carImage`, así que todo carro dado
          de alta desde aquí salía en la vitrina con el título en blanco, sin
          año y con una foto de relleno de un host externo. Ningún chofer la
          tocaba, y el dueño no tenía cómo enterarse: esta pantalla le enseñaba
          su carro leyendo los campos que ella misma había escrito bien.
          Además se saltaba las validaciones que el producto dice que exige
          (año mínimo 2014, A/C, estética y foto obligatoria) y metía un campo
          `id: Math.random()` que no identifica nada.
          Ahora el botón de arriba lleva a Flota, que es el formulario
          completo — y que desde hoy también pide la placa.
          Ver `constants/vehiculoDeFlota.ts`. */}
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    padding: SIZES.padding,
    paddingTop: 60,
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: Colors.text,
  },
  subtitle: {
    fontSize: SIZES.medium,
    color: Colors.textSecondary,
    marginBottom: 30,
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  statCard: {
    flex: 1,
    padding: 20,
    borderRadius: 24,
    marginHorizontal: 5,
    alignItems: 'center',
    overflow: 'hidden',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Colors.text,
    marginTop: 10,
  },
  statLabel: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 5,
  },
  statCardLarge: {
    flex: 1,
    padding: 24,
    borderRadius: 24,
    marginHorizontal: 5,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  cardTitle: {
    fontSize: 16,
    color: Colors.textSecondary,
    marginLeft: 10,
    fontWeight: '600',
  },
  revenueValue: {
    fontSize: 36,
    fontWeight: 'bold',
    color: Colors.text,
  },
  revenueSub: {
    fontSize: 14,
    color: '#4CAF50',
    marginTop: 5,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.text,
    marginTop: 20,
    marginBottom: 15,
  },
  reminderCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderRadius: 20,
    marginBottom: 15,
    overflow: 'hidden',
  },
  reminderTextContainer: {
    flex: 1,
    marginLeft: 15,
  },
  reminderTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: Colors.text,
  },
  reminderSub: {
    fontSize: 13,
    color: Colors.textSecondary,
    marginTop: 4,
  },
  activePlanBadge: {
    backgroundColor: 'rgba(0, 200, 83, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    marginLeft: 'auto',
  },
  activePlanBadgeText: {
    color: '#00C853',
    fontSize: 11,
    fontWeight: 'bold',
  },
  billingRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 10,
    width: '100%',
  },
  billingValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: Colors.text,
  },
  billingSub: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 4,
  },
  manageBillingBtn: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 12,
  },
  manageBillingBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 12,
  },
  chartContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    height: 100,
    marginTop: 25,
    paddingHorizontal: 10,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.1)',
    paddingTop: 15,
  },
  chartBarWrapper: {
    alignItems: 'center',
    width: 25,
  },
  chartBarBg: {
    height: 80,
    width: 12,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 6,
    justifyContent: 'flex-end',
    overflow: 'hidden',
  },
  chartBarFill: {
    width: '100%',
    backgroundColor: '#4CAF50',
    borderRadius: 6,
  },
  chartDayText: {
    fontSize: 12,
    color: Colors.textSecondary,
    marginTop: 8,
    fontWeight: 'bold',
  },
  webBridgeDesc: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginTop: 5,
    marginBottom: 15,
    lineHeight: 20,
  },
  webBridgeSteps: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    padding: 12,
    borderRadius: 12,
    marginBottom: 15,
  },
  webBridgeStepText: {
    fontSize: 13,
    color: Colors.text,
    marginBottom: 4,
  },
  scanButton: {
    backgroundColor: Colors.primary,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
  },
  scanButtonText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  // El panel NO declaraba color de fondo, así que se dibujaba directamente
  // sobre el velo `rgba(0,0,0,0.6)` del modal. Los campos sí se veían (llevan
  // `color: '#FFF'`), pero el título y la X de cerrar usaban `Colors.text`, que
  // es oscuro: texto negro sobre negro. El dueño de rent car abría "Registrar
  // Vehículo", veía los campos sueltos y ni siquiera encontraba cómo cerrarlo.
  // El panel se declara oscuro a propósito, que es como ya estaban los campos.
  modalContent: {
    width: '100%',
    maxWidth: 400,
    // Un tope de alto: sin esto el panel crece con su contenido y se sale de
    // la pantalla por arriba y por abajo a la vez (el velo lo centra), que es
    // como el botón de guardar acabó a 628 px fuera del borde inferior.
    maxHeight: '88%',
    padding: 24,
    borderRadius: 24,
    backgroundColor: '#1E1E1E',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFF',
  },
  modalInputLabel: {
    color: '#FFF',
    marginBottom: 8,
    fontWeight: '600',
  },
  modalInput: {
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    color: '#FFF',
    padding: 15,
    marginBottom: 15,
  },
  saveBtn: {
    backgroundColor: Colors.primary,
    paddingVertical: 15,
    borderRadius: 12,
    alignItems: 'center',
  },
  saveBtnText: {
    color: '#FFF',
    fontWeight: 'bold',
    fontSize: 16,
  }
});

const stylesDesktop = StyleSheet.create({
  container: {
    padding: 40,
    maxWidth: 1400,
    alignSelf: 'center',
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 30,
  },
  profilePicSim: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.1)',
    padding: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 30,
  },
  statCard: {
    flex: 1,
    padding: 24,
    borderRadius: 24,
    alignItems: 'center',
    overflow: 'hidden',
  },
  gridRow: {
    flexDirection: 'row',
    gap: 30,
  },
  leftCol: {
    flex: 1,
    maxWidth: 450,
  },
  rightCol: {
    flex: 2,
  },
  tableCard: {
    padding: 30,
    borderRadius: 24,
    overflow: 'hidden',
    flex: 1,
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    paddingBottom: 15,
    marginBottom: 15,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.05)',
    alignItems: 'center',
  },
  tableCell: {
    fontSize: 14,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  statusText: {
    fontWeight: 'bold',
    fontSize: 12,
  },
  exportBtn: {
    backgroundColor: Colors.accent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    alignSelf: 'flex-end',
    marginTop: 30,
  }
});
